// Responsive Breakpoint Testing and Management
// ============================================

import React from 'react';

// Breakpoint definitions (matching CSS variables)
export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

// Get current breakpoint
export const getCurrentBreakpoint = () => {
  const width = window.innerWidth;
  
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

// Check if current viewport matches breakpoint
export const isBreakpoint = (breakpoint) => {
  const currentBp = getCurrentBreakpoint();
  const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
  const currentIndex = breakpointOrder.indexOf(currentBp);
  const targetIndex = breakpointOrder.indexOf(breakpoint);
  
  return currentIndex >= targetIndex;
};

// Media query helper
export const createMediaQuery = (breakpoint) => {
  return `(min-width: ${breakpoints[breakpoint]}px)`;
};

// React hook for responsive design
export const useBreakpoint = () => {
  const [currentBreakpoint, setCurrentBreakpoint] = React.useState(getCurrentBreakpoint());
  
  React.useEffect(() => {
    const handleResize = () => {
      setCurrentBreakpoint(getCurrentBreakpoint());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return {
    current: currentBreakpoint,
    isXs: currentBreakpoint === 'xs',
    isSm: isBreakpoint('sm'),
    isMd: isBreakpoint('md'),
    isLg: isBreakpoint('lg'),
    isXl: isBreakpoint('xl'),
    is2Xl: isBreakpoint('2xl'),
    isMobile: currentBreakpoint === 'xs' || currentBreakpoint === 'sm',
    isTablet: currentBreakpoint === 'md',
    isDesktop: isBreakpoint('lg')
  };
};

// Responsive value helper
export const useResponsiveValue = (values) => {
  const breakpoint = useBreakpoint();
  
  // Values should be an object like { xs: 'value1', md: 'value2', lg: 'value3' }
  const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
  const currentIndex = breakpointOrder.indexOf(breakpoint.current);
  
  // Find the closest matching value
  for (let i = currentIndex; i >= 0; i--) {
    const bp = breakpointOrder[i];
    if (values[bp] !== undefined) {
      return values[bp];
    }
  }
  
  return values.xs || values[Object.keys(values)[0]];
};

// Breakpoint testing component
export const BreakpointTester = ({ showInProduction = false }) => {
  const breakpoint = useBreakpoint();
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState({ x: 20, y: 20 });
  
  // Only show in development unless explicitly allowed
  if (process.env.NODE_ENV === 'production' && !showInProduction) {
    return null;
  }
  
  const testerStyles = {
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    backgroundColor: '#000000',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '12px',
    fontFamily: 'monospace',
    zIndex: 10000,
    userSelect: 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    minWidth: '200px'
  };
  
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    const handleMouseMove = (e) => {
      setPosition({
        x: e.clientX - offsetX,
        y: e.clientY - offsetY
      });
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  return (
    <div style={testerStyles} onMouseDown={handleMouseDown}>
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
        Breakpoint: {breakpoint.current}
      </div>
      <div style={{ fontSize: '11px', opacity: 0.8 }}>
        {window.innerWidth}px × {window.innerHeight}px
      </div>
      <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>
        {breakpoint.isMobile && '📱 Mobile'}
        {breakpoint.isTablet && '📱 Tablet'}
        {breakpoint.isDesktop && '🖥️ Desktop'}
      </div>
    </div>
  );
};

// Responsive grid helper
export const createResponsiveGrid = (breakpointCols) => {
  const breakpoint = useBreakpoint();
  const cols = useResponsiveValue(breakpointCols);
  
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 'var(--spacing-md)'
  };
};

// Responsive spacing helper
export const useResponsiveSpacing = (spacingMap) => {
  return useResponsiveValue(spacingMap);
};

// Responsive font size helper
export const useResponsiveFontSize = (sizeMap) => {
  return useResponsiveValue(sizeMap);
};

// Touch device detection
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Mobile device detection (more comprehensive)
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return mobileRegex.test(userAgent) || 
         (window.innerWidth <= 768 && isTouchDevice());
};

// Orientation detection
export const useOrientation = () => {
  const [orientation, setOrientation] = React.useState(
    window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  );
  
  React.useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
    };
    
    window.addEventListener('resize', handleOrientationChange);
    return () => window.removeEventListener('resize', handleOrientationChange);
  }, []);
  
  return orientation;
};

// Safe area support
export const useSafeArea = () => {
  const [safeArea, setSafeArea] = React.useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  });
  
  React.useEffect(() => {
    const computedStyle = getComputedStyle(document.documentElement);
    setSafeArea({
      top: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-top)')) || 0,
      right: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-right)')) || 0,
      bottom: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-bottom)')) || 0,
      left: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-left)')) || 0
    });
  }, []);
  
  return safeArea;
};

// Responsive component wrapper
export const ResponsiveWrapper = ({ children, breakpoint, hideBelow = false }) => {
  const currentBreakpoint = useBreakpoint();
  
  const shouldShow = hideBelow ? 
    isBreakpoint(breakpoint) : 
    currentBreakpoint.current === breakpoint;
  
  if (!shouldShow) return null;
  
  return children;
};

// Breakpoint-based component switching
export const BreakpointSwitch = ({ mobile, tablet, desktop }) => {
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  
  if (isMobile && mobile) return mobile;
  if (isTablet && tablet) return tablet;
  if (isDesktop && desktop) return desktop;
  
  return mobile || tablet || desktop;
};

// Container query polyfill helper
export const useContainerQuery = (containerRef, query) => {
  const [matches, setMatches] = React.useState(false);
  
  React.useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        
        // Simple width-based queries for now
        if (query.includes('min-width')) {
          const minWidth = parseInt(query.match(/min-width:\s*(\d+)px/)?.[1]);
          setMatches(width >= minWidth);
        } else if (query.includes('max-width')) {
          const maxWidth = parseInt(query.match(/max-width:\s*(\d+)px/)?.[1]);
          setMatches(width <= maxWidth);
        }
      }
    });
    
    observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, [containerRef, query]);
  
  return matches;
};

// Responsive test suite
export const runResponsiveTests = () => {
  const tests = [
    {
      name: 'Mobile breakpoint (< 640px)',
      test: () => window.innerWidth < 640,
      expected: getCurrentBreakpoint() === 'xs'
    },
    {
      name: 'Tablet breakpoint (640px - 1024px)',
      test: () => window.innerWidth >= 640 && window.innerWidth < 1024,
      expected: ['sm', 'md'].includes(getCurrentBreakpoint())
    },
    {
      name: 'Desktop breakpoint (≥ 1024px)',
      test: () => window.innerWidth >= 1024,
      expected: ['lg', 'xl', '2xl'].includes(getCurrentBreakpoint())
    },
    {
      name: 'Touch device detection',
      test: () => isTouchDevice(),
      expected: true // Will vary by device
    }
  ];
  
  const results = tests.map(test => ({
    name: test.name,
    passed: test.test() === test.expected,
    actual: test.test(),
    expected: test.expected
  }));
  
  console.table(results);
  return results;
};

// Export all utilities
export default {
  breakpoints,
  getCurrentBreakpoint,
  isBreakpoint,
  createMediaQuery,
  useBreakpoint,
  useResponsiveValue,
  BreakpointTester,
  createResponsiveGrid,
  useResponsiveSpacing,
  useResponsiveFontSize,
  isTouchDevice,
  isMobileDevice,
  useOrientation,
  useSafeArea,
  ResponsiveWrapper,
  BreakpointSwitch,
  useContainerQuery,
  runResponsiveTests
};